import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, 
  List, ListItem, ListItemIcon, ListItemText, Divider, CircularProgress, 
  Chip, Avatar, IconButton, Tabs, Tab, Pagination, ListItemAvatar,
  Paper, Grid, Tooltip,
  Collapse
} from '@mui/material';
import moment from 'moment';
import { useAlert } from '../../contexts/AlertContext';
import ActivityTimeline from './ActivityTimeline';

import { 
  Login as LoginIcon,
  Logout as LogoutIcon,
  AddCircle as CreateIcon, 
  Edit as UpdateIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Code as RendererActionIcon,
  Person as UserIcon,
  Movie as MovieIcon,
  ViewInAr as ViewInArIcon,
  InsertDriveFile as AssetIcon,
  Business as OrganizationIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  Article as DocumentIcon,
  Folder as FolderIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ViewList as ViewListIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import apiService from '../../services/api.service';

const activityIcons = {
  LOGIN: <LoginIcon color="success" />,
  LOGOUT: <LogoutIcon color="info" />,
  CREATE: <CreateIcon color="primary" />,
  UPDATE: <UpdateIcon color="secondary" />,
  DELETE: <DeleteIcon color="error" />,
  VIEW: <ViewIcon color="default" />,
  UPLOAD: <UploadIcon color="primary" />,
  RENDERER_ACTION: <RendererActionIcon color="warning" />
};

const resourceIcons = {
  user: <UserIcon />,
  scene: <MovieIcon />,
  renderer: <ViewInArIcon />,
  asset: <AssetIcon />,
  organization: <OrganizationIcon />
};

const getFileTypeIcon = (filename) => {
  if (!filename) return <AssetIcon />;
  
  const extension = filename.split('.').pop().toLowerCase();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
  const videoExtensions = ['mp4', 'webm', 'avi', 'mov', 'wmv', 'flv', 'mkv'];
  const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx', 'ppt', 'pptx'];
  const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml', 'py', 'java', 'c', 'cpp'];
  
  if (imageExtensions.includes(extension)) return <ImageIcon color="primary" />;
  if (videoExtensions.includes(extension)) return <VideoIcon color="secondary" />;
  if (documentExtensions.includes(extension)) return <DocumentIcon color="info" />;
  if (codeExtensions.includes(extension)) return <CodeIcon color="warning" />;
  
  return <AssetIcon />;
};

const parseResourcePath = (resourceId) => {
  if (!resourceId || typeof resourceId !== 'string') return { sceneName: '', folderPath: '', fileName: '' };
    const parts = resourceId.split('/');
    if (parts.length < 3) return { sceneName: '', folderPath: '', fileName: resourceId };
    if (parts[0] === 'scenes') {
    const sceneName = parts[1];
    const fileName = parts[parts.length - 1];
    const folderPath = parts.slice(2, parts.length - 1).join('/');
    
    return { sceneName, folderPath, fileName };
  }
  
  return { 
    sceneName: '', 
    folderPath: resourceId.substring(0, resourceId.lastIndexOf('/')), 
    fileName: resourceId.substring(resourceId.lastIndexOf('/') + 1)
  };
};

const ActivityTrackingModal = ({ 
  open, 
  onClose, 
  userId = null, 
  orgId = null, 
  title = null,
  showUserSelector = false ,
  showOrgActivities = true, 
  defaultViewType = null     

}) => {
  const [allActivities, setAllActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState(defaultViewType || (userId ? 'user' : 'organization'));
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [displayMode, setDisplayMode] = useState('list'); 
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [standardLimit, setStandardLimit] = useState(10);
  const [timelineLimit, setTimelineLimit] = useState(30); 
  
  const alert = useAlert();

  const handleDisplayModeChange = (event, newMode) => {
    if (newMode !== displayMode) {
      setDisplayMode(newMode);
      setExpandedActivity(null); 
      setAllActivities([]);
      fetchActivities(1);
    }
  };
  useEffect(() => {
    if (!showOrgActivities && userId) {
      setViewType('user');
    }
  }, [showOrgActivities, userId]);

  
  const fetchActivities = async (page = 1, append = false) => {
    if (loadingMore) {
      return;
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const limit = displayMode === 'timeline' ? timelineLimit : standardLimit;
      const params = {
        page: page,
        limit: limit
      };
      
      let data;
      if (viewType === 'user' && userId) {
        data = await apiService.getUserActivityLogs(userId, params);
      } else if (orgId) {
        data = await apiService.getActivityLogs(orgId, params);
      } else {
        throw new Error('Missing userId or orgId');
      }
      
      const result = data.data || data; 
      
      console.log('Fetched activities:', result);
      if (append) {
        setAllActivities(prevActivities => [...prevActivities, ...(result.activities || [])]);
      } else {
        setAllActivities(result.activities || []);
      }
      
      const paginationData = result.pagination || {};
      
      setPagination({
        total: paginationData.total || 0,
        page: paginationData.page || 1,
        limit: paginationData.limit || 10,
        pages: paginationData.pages || 1
      });
      
      setCurrentPage(paginationData.page || 1);
      setHasMore(paginationData.page < paginationData.pages);
      
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError(`Failed to load activity data: ${error.message}`);
      alert.error(`Failed to load activity data: ${error.message}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      fetchActivities(currentPage + 1, true);
    }
  };
  
  useEffect(() => {
    if (open) {
      fetchActivities(1);
      setCurrentPage(1);
    }
  }, [open, viewType, userId, orgId]);
  
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    fetchActivities(page);
  };
  
  const handleRefresh = () => {
    fetchActivities(currentPage);
  };
  
  const handleTabChange = (event, newValue) => {
    setViewType(newValue);
  };
  
  const formatDate = (timestamp) => {
    try {
      return moment(timestamp).format("MMM D, YYYY [at] h:mm a");
    } catch (e) {
      return timestamp || 'Unknown date';
    }
  };
  
  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'LOGIN': return 'success.main';
      case 'LOGOUT': return 'info.main';
      case 'CREATE': return 'primary.main';
      case 'UPDATE': return 'secondary.main';
      case 'DELETE': return 'error.main';
      case 'VIEW': return 'text.primary';
      case 'UPLOAD': return 'primary.dark';
      case 'RENDERER_ACTION': return 'warning.main';
      default: return 'text.primary';
    }
  };
  
  
  const toggleActivityExpansion = (activityId) => {
    setExpandedActivity(expandedActivity === activityId ? null : activityId);
  };
  
  const renderActivityDetails = (activity) => {
    const isAsset = activity.resource_type === 'asset';
    const { sceneName, folderPath, fileName } = isAsset ? 
      parseResourcePath(activity.resource_id) : 
      { sceneName: '', folderPath: '', fileName: '' };
    
    const fileIcon = isAsset ? getFileTypeIcon(fileName || activity.resource_name) : null;
    
    return (
      <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: "#212121" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {activity.resource_type.charAt(0).toUpperCase() + activity.resource_type.slice(1)} Details:
            </Typography>
          </Grid>
          
          {/* Organization info if available */}
          {activity.organization_name && (
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OrganizationIcon fontSize="small" color="primary" />
                <Typography variant="body2">
                  <Typography component="span" color="text.secondary" variant="body2">Organization: </Typography>
                  {activity.organization_name}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* Scene name for assets */}
          {isAsset && sceneName && (
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MovieIcon fontSize="small" color="info" />
                <Typography variant="body2">
                  <Typography component="span" color="text.secondary" variant="body2">Scene: </Typography>
                  {sceneName}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* Folder path for assets */}
          {isAsset && folderPath && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderIcon fontSize="small" color="warning" />
                <Tooltip title={folderPath} arrow>
                  <Typography variant="body2" sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Typography component="span" color="text.secondary" variant="body2">Path: </Typography>
                    {folderPath || 'Root folder'}
                  </Typography>
                </Tooltip>
              </Box>
            </Grid>
          )}
          
          {/* File info for assets */}
          {isAsset && (
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {fileIcon || <AssetIcon />}
                <Typography variant="body2">
                  <Typography component="span" color="text.secondary" variant="body2">File: </Typography>
                  {fileName || activity.resource_name}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* Additional details from activity.additionalDetails  */}
          {activity.additionalDetails && Object.keys(activity.additionalDetails).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Additional Info:</Typography>
              {Object.entries(activity.additionalDetails).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1, minWidth: '120px' }}>
                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                  </Typography>
                  <Typography variant="body2">
                    {typeof value === 'object' ? JSON.stringify(value) : value.toString()}
                  </Typography>
                </Box>
              ))}
            </Grid>
          )}
          
          {/* IP Address */}
          {activity.ip_address && (
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" />
                <Typography variant="body2">
                  <Typography component="span" color="text.secondary" variant="body2">IP Address: </Typography>
                  {activity.ip_address}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {/* Activity type specific info */}
          <Grid item xs={12}>
            <Chip 
              size="small"
              color={
                activity.activity_type === 'DELETE' ? 'error' :
                activity.activity_type === 'CREATE' ? 'success' :
                activity.activity_type === 'UPDATE' ? 'primary' : 'default'
              }
              variant="outlined"
              label={`${
                activity.activity_type === 'CREATE' ? 'Created' :
                activity.activity_type === 'UPDATE' ? 'Modified' :
                activity.activity_type === 'DELETE' ? 'Deleted' :
                activity.activity_type
              } by ${activity.user_name}`}
              icon={activityIcons[activity.activity_type] || <ViewIcon fontSize="small" />}
              sx={{ mt: 1 }}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
    border={6}
    borderColor="#FF455C"
     
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
            {title || (viewType === 'user' ? 'User Activity History' : 'Organization Activity History')}
          </Typography>          
        </Box>
        
        {(showUserSelector && (showOrgActivities || userId)) && (
          <Tabs 
            value={viewType} 
            onChange={handleTabChange} 
            sx={{ mt: 1, borderColor: 'divider' }}
          >
            {showOrgActivities && orgId && <Tab value="organization" label="Organization Activities" />}
          </Tabs>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ minHeight: 800 }}>
        {loading && !loadingMore ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : allActivities.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography>No activity records found.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ position: 'absolute', top: 12, right: 48, display: 'flex', alignItems: 'center', border: 1, borderRadius:3, borderColor: '#FF455C'  }}>
                <Tooltip title="List View">
                    <IconButton 
                        color={displayMode === 'list' ? 'primary' : 'default'} 
                        onClick={() => handleDisplayModeChange(null, 'list')}
                        sx={{ color: displayMode === 'list' ? '#FF455C'  : 'white' }}
                    >
                        <ViewListIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Timeline View">
                    <IconButton 
                        color={displayMode === 'timeline' ? 'primary' : 'default'} 
                        onClick={() => handleDisplayModeChange(null, 'timeline')}
                        sx={{ color: displayMode === 'timeline' ? '#FF455C' : 'white' }}
                    >
                        <TimelineIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Timeline View">
                <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
                </Tooltip>

               
            </Box>

            {/* List View */}
            {displayMode === 'list' && (
              <List>
                {allActivities.map((activity) => (
                  <React.Fragment key={activity._id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        flexDirection: 'column',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          width: '100%',
                          alignItems: 'flex-start'
                        }}
                        onClick={() => toggleActivityExpansion(activity._id)}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: getActivityColor(activity.activity_type) }}>
                            {activityIcons[activity.activity_type] || <ViewIcon />}
                          </Avatar>
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              <Typography component="span" variant="body1" fontWeight="bold">
                                {activity.user_name}
                              </Typography>
                              <Chip 
                                label={activity.activity_type} 
                                size="small" 
                                color={
                                  activity.activity_type === 'DELETE' ? 'error' :
                                  activity.activity_type === 'CREATE' ? 'success' : 'default'
                                } 
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {activity.resource_type === 'asset' ? 
                                  getFileTypeIcon(activity.resource_name) : 
                                  resourceIcons[activity.resource_type] || <AssetIcon />
                                }
                                <Typography component="span" variant="body2" fontWeight="bold">
                                  {activity.resource_name || activity.resource_type}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                              <Typography component="span" variant="body2" color="text.secondary">
                                {formatDate(activity.timestamp)}
                              </Typography>
                              
                              <Button 
                                size="small"
                                variant=""
                                endIcon={expandedActivity === activity._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleActivityExpansion(activity._id);
                                }}
                                sx={{ ml: 2, color: 'text.primary' }}
                              >
                              </Button>
                            </Box>
                          }
                        />
                      </Box>
                      
                      {/* Collapsible activity details */}
                      <Collapse in={expandedActivity === activity._id} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                        {renderActivityDetails(activity)}
                      </Collapse>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
            
            {/* Timeline View */}
            {displayMode === 'timeline' && (
              <ActivityTimeline 
                activities={allActivities}
                onLoadMore={handleLoadMore}
                hasMoreActivities={hasMore}
                loadingMore={loadingMore}
                totalItems={pagination.total}
              />
            )}
            
            {/* Pagination */}
            {displayMode === 'list' && pagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                <Pagination 
                  count={pagination.pages} 
                  page={currentPage} 
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityTrackingModal;